/**
 * The relief's surface, as a patched `MeshPhysicalMaterial`.
 *
 * The previous material computed its own lighting: `(0.86 + 0.2 * lambert) *
 * bakedAmbient`, clamped to low dynamic range, with a hand-rolled warm/cool
 * tint standing in for everything a room does to a surface. No amount of tuning
 * takes that to photographic, so the data is wired into three's BRDF instead and
 * inherits image-based lighting, shadow maps, a Fresnel term, clearcoat and
 * energy-conserving multiscatter for free.
 *
 * Four things are injected that a stock material cannot express:
 *
 *  - **Displacement at a chosen mip level.** The vertex reads the max-preserving
 *    height chain, and the fragment builds its normal from *the same level*. The
 *    old shader filtered its normal over five texels while the geometry jumped by
 *    one and a half, which is precisely why the spires read as glass shards:
 *    smooth shading stretched over a jagged silhouette.
 *  - **An analytic coastline** from the signed distance field, resolved to
 *    fractional coverage instead of discarded per whole cell.
 *  - **Slope detail below the sampling scale turned into roughness**, so a city
 *    that goes sub-pixel becomes a rougher, quieter patch rather than a field of
 *    flickering highlights. Same idea for the cast surface's micro-relief, which
 *    fades into roughness as it stops being resolvable.
 *  - **Baked visibility**: ambient occlusion, a bent normal for the diffuse
 *    environment lookup, one bounce of coloured inter-reflection, and a thickness
 *    channel driving gypsum's short-range translucency.
 *
 * The chunk replacements below assume this material's own feature set — no
 * lightmap, no anisotropy, no tangent-space normal map. That is a deliberate
 * trade: reproducing every branch of the stock chunks would obscure what is
 * actually different.
 */

import * as THREE from "three";

import {
  GRAIN_CELLS,
  GRAIN_SLOPE,
  RELIEF_ROUGHNESS_RURAL,
  RELIEF_ROUGHNESS_URBAN,
  RELIEF_SPECULAR,
  RELIEF_TRANSLUCENCY,
} from "../../shared/constants.ts";
import type { ReliefFields } from "../../shared/types.ts";
import type { ReliefPalettes } from "./palette.ts";

export interface ReliefTextures {
  /** R32F height with a max-preserving manual mip chain. */
  height: THREE.DataTexture;
  /** RGBA8: population, terrain, encoded coast distance, cosine of slope. */
  shade: THREE.DataTexture;
  /** RGBA16F: ambient occlusion, bent normal xy, thickness. */
  visibility: THREE.Texture;
  /** RGB16F: one bounce of indirect colour. */
  bounce: THREE.Texture;
}

export interface ReliefMaterials {
  surface: THREE.MeshPhysicalMaterial;
  /** Same displacement and the same coastline, for the shadow map's depth pass. */
  depth: THREE.MeshDepthMaterial;
  /** Geometry of the current field: extent, cell size, resolution, coast range. */
  setField(fields: ReliefFields, coastRange: number): void;
  setTextures(textures: ReliefTextures): void;
  /** Mip level the geometry resolves; the fragment normal follows it. */
  setLod(level: number): void;
  dispose(): void;
}

const sharedDeclarations = /* glsl */ `
uniform sampler2D uHeight;
uniform vec4 uExtent;
uniform vec2 uFieldSize;
uniform float uGeomLod;
varying vec2 vGrid;
`;

const vertexDisplacement = /* glsl */ `
  vGrid = vec2(
    (position.x - uExtent.x) / (uExtent.y - uExtent.x),
    (uExtent.w - position.y) / (uExtent.w - uExtent.z)
  );
  // The mesh is built so that its vertices land exactly on the texel centres of
  // this mip level, so a linear fetch here returns the texel itself and
  // interpolates between texels in step with the geometry's own interpolation.
  vec3 transformed = vec3(position.x, position.y, textureLod(uHeight, vGrid, uGeomLod).r);
`;

/** Coverage from the distance field; shared by the surface and the depth pass. */
const coastCoverage = /* glsl */ `
float reliefCoverage(vec4 shade) {
  float signedDistance = (shade.b * 2.0 - 1.0) * uCoastRange;
  // |grad sdf| is 1 in world units, so a pixel's world footprint is exactly the
  // width the edge should be resolved over.
  float footprint = max(
    max(length(dFdx(vGrid) * uSpan), length(dFdy(vGrid) * uSpan)),
    1e-7
  );
  return clamp(signedDistance / footprint + 0.5, 0.0, 1.0);
}
`;

const fragmentDeclarations = /* glsl */ `
uniform sampler2D uShade;
uniform sampler2D uVisibility;
uniform sampler2D uBounce;
uniform sampler2D uTerrainPalette;
uniform sampler2D uPopulationPalette;
uniform vec2 uSpan;
uniform vec2 uCellSize;
uniform float uCoastRange;
uniform vec2 uRoughnessRange;
uniform float uTranslucency;
uniform vec2 uGrain;

vec3 reliefWorldNormal;
vec3 reliefBentView;
vec3 reliefBounce;
float reliefAo;
float reliefThickness;
float reliefExtraSlope;
float reliefPopulation;

${coastCoverage}

float reliefHash(vec2 point) {
  vec3 spread = fract(vec3(point.xyx) * vec3(0.1031, 0.1030, 0.0973));
  spread += dot(spread, spread.yzx + 33.33);
  return fract((spread.x + spread.y) * spread.z);
}

float reliefValueNoise(vec2 point) {
  vec2 cell = floor(point);
  vec2 offset = fract(point);
  vec2 weight = offset * offset * (3.0 - 2.0 * offset);
  float lowerLeft = reliefHash(cell);
  float lowerRight = reliefHash(cell + vec2(1.0, 0.0));
  float upperLeft = reliefHash(cell + vec2(0.0, 1.0));
  float upperRight = reliefHash(cell + vec2(1.0, 1.0));
  return mix(
    mix(lowerLeft, lowerRight, weight.x),
    mix(upperLeft, upperRight, weight.x),
    weight.y
  );
}

/** Two octaves of the cast surface's tooth. */
float reliefGrain(vec2 point) {
  return reliefValueNoise(point) + 0.5 * reliefValueNoise(point * 2.03 + 7.13);
}

/** Forward difference: three evaluations rather than five for the same slope. */
vec2 reliefGrainGradient(vec2 point) {
  const float step = 0.4;
  float centre = reliefGrain(point);
  return vec2(
    reliefGrain(point + vec2(step, 0.0)) - centre,
    reliefGrain(point + vec2(0.0, step)) - centre
  ) / step;
}
`;

const fragmentSurface = /* glsl */ `
  vec4 shade = texture2D(uShade, vGrid);

  // Silhouette first, so nothing else is computed for open sea. Fractional
  // coverage plus multisampling resolves the coast analytically; the old whole-cell
  // discard left the staircase that traces every shore in the reference captures.
  float coverage = reliefCoverage(shade);
  if (coverage <= 0.0) discard;
  diffuseColor.a = coverage;

  // Normal from the very level the vertex displaced, so shading and silhouette
  // describe one surface rather than two.
  vec2 texel = exp2(uGeomLod) / uFieldSize;
  vec2 spacing = uCellSize * exp2(uGeomLod);
  float west = textureLod(uHeight, vGrid - vec2(texel.x, 0.0), uGeomLod).r;
  float east = textureLod(uHeight, vGrid + vec2(texel.x, 0.0), uGeomLod).r;
  float north = textureLod(uHeight, vGrid - vec2(0.0, texel.y), uGeomLod).r;
  float south = textureLod(uHeight, vGrid + vec2(0.0, texel.y), uGeomLod).r;
  reliefWorldNormal = normalize(vec3(
    -(east - west) / (2.0 * spacing.x),
    -(north - south) / (2.0 * spacing.y),
    1.0
  ));

  // How many grid cells this pixel covers, which is what decides how much detail
  // has to become roughness instead of geometry.
  vec2 acrossX = dFdx(vGrid) * uFieldSize;
  vec2 acrossY = dFdy(vGrid) * uFieldSize;
  float pixelLod = 0.5 * log2(max(max(dot(acrossX, acrossX), dot(acrossY, acrossY)), 1.0));

  // Mean cosine of slope over the pixel against the cosine the geometry resolves:
  // the difference is slope that exists but is no longer represented. Converting
  // it to roughness is what stops a sub-pixel city from glittering.
  float cosPixel = clamp(textureLod(uShade, vGrid, pixelLod).a, 0.02, 1.0);
  float slopePixel = sqrt(max(1.0 / (cosPixel * cosPixel) - 1.0, 0.0));
  float slopeGeom = sqrt(max(1.0 / (reliefWorldNormal.z * reliefWorldNormal.z) - 1.0, 0.0));
  reliefExtraSlope = max(slopePixel - slopeGeom, 0.0);

  // Micro-relief of the casting, in grid space so the light sees it, it
  // foreshortens, and it stays put when the camera orbits — none of which a
  // screen-space grain overlay can do. As it stops being resolvable it hands
  // itself over to roughness rather than aliasing.
  float cellsPerPixel = exp2(pixelLod);
  float grainFade = clamp(1.0 - cellsPerPixel / uGrain.x, 0.0, 1.0);
  if (grainFade > 0.0) {
    vec2 gradient = reliefGrainGradient(vGrid * uFieldSize / uGrain.x);
    reliefWorldNormal =
      normalize(reliefWorldNormal + vec3(gradient * uGrain.y * grainFade, 0.0));
  }
  reliefExtraSlope += (1.0 - grainFade) * uGrain.y;

  reliefPopulation = clamp(shade.r, 0.0, 1.0);
  vec3 stone = texture2D(uTerrainPalette, vec2(clamp(shade.g, 0.0, 1.0), 0.5)).rgb;
  vec3 ink = texture2D(uPopulationPalette, vec2(reliefPopulation, 0.5)).rgb;
  diffuseColor.rgb = mix(stone, ink, smoothstep(0.008, 0.2, reliefPopulation));

  vec4 visibility = texture2D(uVisibility, vGrid);
  reliefAo = clamp(visibility.r, 0.0, 1.0);
  // The bent normal is a hemisphere average, so its z is never negative on a
  // height field and two channels carry it.
  vec2 bentFlat = visibility.gb;
  vec3 bentWorld = vec3(bentFlat, sqrt(max(1.0 - dot(bentFlat, bentFlat), 0.0)));
  reliefBentView = normalize((viewMatrix * vec4(bentWorld, 0.0)).xyz);
  reliefThickness = clamp(visibility.a, 0.0, 1.0);
  reliefBounce = texture2D(uBounce, vGrid).rgb;
`;

const fragmentRoughness = /* glsl */ `
  // Rough on quiet ground, a little glossier where the cast pooled in dense
  // cores, and rougher again wherever slope was lost below the sampling scale.
  float roughnessFactor =
    mix(uRoughnessRange.x, uRoughnessRange.y, smoothstep(0.05, 0.55, reliefPopulation));
  // Variance adds in alpha, not in perceptual roughness: alpha = roughness^2 and
  // alpha_total^2 = alpha_base^2 + 2 sigma^2.
  float alphaBase = roughnessFactor * roughnessFactor;
  float alpha = sqrt(alphaBase * alphaBase + 2.0 * reliefExtraSlope * reliefExtraSlope);
  roughnessFactor = clamp(sqrt(min(alpha, 1.0)), 0.04, 1.0);
`;

const fragmentNormal = /* glsl */ `
  normal = normalize((viewMatrix * vec4(reliefWorldNormal, 0.0)).xyz);
  nonPerturbedNormal = normal;
`;

/**
 * Diffuse environment light follows the bent normal: between two spires the
 * visible sky is not centred on the geometric normal, and pretending otherwise is
 * what makes a dense relief look like a flat print of itself.
 */
const fragmentIndirect = /* glsl */ `
#if defined( RE_IndirectDiffuse )
  #if defined( USE_ENVMAP ) && defined( ENVMAP_TYPE_CUBE_UV )
    iblIrradiance += getIBLIrradiance( reliefBentView );
  #endif
#endif

#if defined( USE_ENVMAP ) && defined( RE_IndirectSpecular )
  radiance += getIBLRadiance( geometryViewDir, geometryNormal, material.roughness );
  #ifdef USE_CLEARCOAT
    clearcoatRadiance += getIBLRadiance( geometryViewDir, geometryClearcoatNormal, material.clearcoatRoughness );
  #endif
#endif
`;

const fragmentOcclusion = /* glsl */ `
  float ambientOcclusion = reliefAo;
  reflectedLight.indirectDiffuse *= ambientOcclusion;
  #if defined( USE_ENVMAP ) && defined( STANDARD )
    float dotNV = saturate( dot( geometryNormal, geometryViewDir ) );
    reflectedLight.indirectSpecular *=
      computeSpecularOcclusion( dotNV, ambientOcclusion, material.roughness );
  #endif

  // The bounce is light that arrives *because* the neighbours are there, so it is
  // added after occlusion rather than attenuated by it.
  reflectedLight.indirectDiffuse += reliefBounce * BRDF_Lambert( material.diffuseColor );

  #if NUM_DIR_LIGHTS > 0
    // Gypsum transports light a millimetre or two, so a thin spire lit from
    // behind glows at its edge while massive ground does not. Lateral support is
    // how much material stands behind the surface, which is why the effect finds
    // the spires on its own.
    vec3 throughDirection = normalize(directionalLights[0].direction + normal * 0.35);
    float through = pow(saturate(dot(geometryViewDir, -throughDirection)), 4.0);
    reflectedLight.indirectDiffuse += directionalLights[0].color * material.diffuseColor *
      (uTranslucency * through * (1.0 - reliefThickness));
  #endif
`;

const depthFragment = /* glsl */ `
  // The shadow caster has to end where the relief ends, or every coastline throws
  // a shadow from sea that is not there. No coverage blending: a shadow map is
  // sampled with hardware comparison, so a fragment either occludes or does not.
  if (reliefCoverage(texture2D(uShade, vGrid)) < 0.5) discard;
`;

const depthDeclarations = /* glsl */ `
uniform sampler2D uShade;
uniform vec2 uSpan;
uniform float uCoastRange;

${coastCoverage}
`;

/**
 * Both materials, sharing one uniform object so a texture swap, a field swap or
 * an LOD change is a single assignment rather than a hunt through two shaders.
 *
 * Nothing about a particular country is baked in at construction, which is what
 * lets the preview stages and the finished render — different grid sizes, different
 * extents — pass through the same compiled program. Recompiling a physical shader
 * mid-stream would stall the very moment the picture is meant to be settling.
 */
export function createReliefMaterials(palettes: ReliefPalettes): ReliefMaterials {
  const uniforms: Record<string, THREE.IUniform> = {
    uHeight: { value: null },
    uShade: { value: null },
    uVisibility: { value: null },
    uBounce: { value: null },
    uTerrainPalette: { value: palettes.terrain },
    uPopulationPalette: { value: palettes.population },
    uExtent: { value: new THREE.Vector4(-0.5, 0.5, -0.5, 0.5) },
    uSpan: { value: new THREE.Vector2(1, 1) },
    uCellSize: { value: new THREE.Vector2(1, 1) },
    uFieldSize: { value: new THREE.Vector2(1, 1) },
    uGeomLod: { value: 0 },
    uCoastRange: { value: 1 },
    uRoughnessRange: {
      value: new THREE.Vector2(RELIEF_ROUGHNESS_RURAL, RELIEF_ROUGHNESS_URBAN),
    },
    uTranslucency: { value: RELIEF_TRANSLUCENCY },
    uGrain: { value: new THREE.Vector2(GRAIN_CELLS, GRAIN_SLOPE) },
  };

  const surface = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    roughness: RELIEF_ROUGHNESS_RURAL,
    metalness: 0,
    specularIntensity: RELIEF_SPECULAR,
    side: THREE.FrontSide,
    // Fractional coverage resolved by the multisample mask: no blending, no sort
    // order, and the temporal accumulation upstream turns four levels into a
    // continuous edge.
    alphaToCoverage: true,
    dithering: false,
  });
  surface.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = sharedDeclarations + shader.vertexShader;
    shader.vertexShader = shader.vertexShader
      .replace("#include <begin_vertex>", vertexDisplacement)
      // The plane's normal attribute is never read — the fragment derives the
      // normal from the height field — so it is pinned here and the buffer is
      // dropped from the geometry, which is nineteen megabytes at screen LOD.
      .replace("#include <beginnormal_vertex>", "vec3 objectNormal = vec3(0.0, 0.0, 1.0);");
    shader.fragmentShader = sharedDeclarations + fragmentDeclarations + shader.fragmentShader;
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <map_fragment>", fragmentSurface)
      .replace("#include <roughnessmap_fragment>", fragmentRoughness)
      .replace("#include <normal_fragment_begin>", `#include <normal_fragment_begin>${fragmentNormal}`)
      .replace("#include <lights_fragment_maps>", fragmentIndirect)
      .replace("#include <aomap_fragment>", fragmentOcclusion);
  };

  const depth = new THREE.MeshDepthMaterial();
  depth.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = sharedDeclarations + shader.vertexShader;
    shader.vertexShader = shader.vertexShader
      .replace("#include <begin_vertex>", vertexDisplacement)
      .replace("#include <beginnormal_vertex>", "vec3 objectNormal = vec3(0.0, 0.0, 1.0);");
    shader.fragmentShader = sharedDeclarations + depthDeclarations + shader.fragmentShader;
    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <map_fragment>",
      depthFragment,
    );
  };

  return {
    surface,
    depth,

    setField(fields: ReliefFields, coastRange: number): void {
      const [minX, maxX, minY, maxY] = fields.extent;
      (uniforms.uExtent.value as THREE.Vector4).set(minX, maxX, minY, maxY);
      (uniforms.uSpan.value as THREE.Vector2).set(maxX - minX, maxY - minY);
      (uniforms.uCellSize.value as THREE.Vector2).set(fields.cellX, fields.cellY);
      (uniforms.uFieldSize.value as THREE.Vector2).set(fields.width, fields.height);
      uniforms.uCoastRange.value = coastRange;
    },

    setTextures(textures: ReliefTextures): void {
      uniforms.uHeight.value = textures.height;
      uniforms.uShade.value = textures.shade;
      uniforms.uVisibility.value = textures.visibility;
      uniforms.uBounce.value = textures.bounce;
    },

    setLod(level: number): void {
      uniforms.uGeomLod.value = level;
    },

    dispose(): void {
      surface.dispose();
      depth.dispose();
    },
  };
}
